/**
 * Branding Settings (admin) - every field here is applied site-wide:
 * header, footer, login page, admin panel, all Tailwind brand tokens (buttons,
 * links, accents, backgrounds), fonts, favicon, emails and certificates.
 *
 * Saving dispatches 'branding-updated' so the change shows immediately.
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Palette, Upload, Save, Loader2, RotateCcw, Image as ImageIcon, Type, Square } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { API_BASE } from '../../config/api';
import { useTenantBranding, DEFAULT_BRANDING } from '../../contexts/TenantBrandingContext';

const API = API_BASE;

const FONTS = ['Inter', 'Outfit', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins',
  'Source Sans 3', 'Nunito', 'Raleway', 'Merriweather', 'Playfair Display'];
const BUTTON_STYLES = [
  { id: 'rounded', label: 'Rounded', radius: '6px' },
  { id: 'pill', label: 'Pill', radius: '9999px' },
  { id: 'square', label: 'Square', radius: '0px' },
];
const COLOR_PRESETS = ['#C8A85A', '#D4AF37', '#1EBFB3', '#3B82F6', '#8B5CF6', '#EF4444', '#F97316', '#10B981'];

const EDITABLE = ['site_name', 'header_tagline', 'footer_text', 'support_email', 'logo_url', 'favicon_url',
  'login_background_url', 'primary_color', 'secondary_color', 'accent_color', 'heading_font', 'body_font', 'button_style'];

// Form shows only what the admin customised; empty = default (so defaults never get "pinned")
const pick = (b) => EDITABLE.reduce((acc, k) => {
  const custom = (b?.customised_fields || []).includes(k);
  const legacyLogo = k === 'logo_url' && b?.logo_url && b.logo_url !== DEFAULT_BRANDING.logo_url;
  return { ...acc, [k]: custom || legacyLogo ? (b?.[k] ?? '') : '' };
}, {});

function ColorField({ id, label, value, onChange, help }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-zinc-300">{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000'}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="h-10 w-12 rounded border border-zinc-700 bg-transparent cursor-pointer"
          data-testid={`${id}-picker`}
        />
        <Input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#C8A85A"
          className="bg-zinc-900 border-zinc-700 text-white font-mono w-32"
          data-testid={`${id}-input`}
        />
      </div>
      {help && <p className="text-xs text-zinc-500">{help}</p>}
    </div>
  );
}

export default function BrandingSettings({ token, organizationId }) {
  const { refreshBranding } = useTenantBranding();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [saved, setSaved] = useState(pick(DEFAULT_BRANDING));
  const [form, setForm] = useState(pick(DEFAULT_BRANDING));
  const [updatedAt, setUpdatedAt] = useState(null);
  const fileRefs = { logo: useRef(null), favicon: useRef(null), login_background: useRef(null) };

  useEffect(() => {
    if (!organizationId) return;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/organizations/${organizationId}/branding`, { params: { _: Date.now() } });
        setSaved(pick(res.data));
        setForm(pick(res.data));
        setUpdatedAt(res.data.updated_at);
      } catch (e) {
        toast.error('Failed to load branding');
      } finally {
        setLoading(false);
      }
    })();
  }, [organizationId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const changed = EDITABLE.filter((k) => (form[k] || '') !== (saved[k] || ''));

  const handleUpload = async (kind, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('File too large. Max 2MB'); return; }
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/organizations/${organizationId}/branding/logo?kind=${kind}`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const field = `${kind}_url`;
      setForm((f) => ({ ...f, [field]: res.data.url }));
      setSaved((s) => ({ ...s, [field]: res.data.url }));
      await refreshBranding();
      window.dispatchEvent(new Event('branding-updated'));
      toast.success('Image uploaded and applied');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = changed.reduce((acc, k) => ({ ...acc, [k]: form[k] || '' }), {});
      const res = await axios.put(`${API}/api/organizations/${organizationId}/branding`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSaved(pick(res.data.branding));
      setForm(pick(res.data.branding));
      setUpdatedAt(res.data.branding?.updated_at);
      await refreshBranding();
      window.dispatchEvent(new Event('branding-updated'));
      toast.success('Branding saved - applied across the site');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const resetField = (k) => set(k, '');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-navigate-gold" />
      </div>
    );
  }

  const radius = BUTTON_STYLES.find((b) => b.id === form.button_style)?.radius || '6px';
  const previewPrimary = /^#[0-9a-fA-F]{6}$/.test(form.primary_color) ? form.primary_color : DEFAULT_BRANDING.primary_color;
  const previewSecondary = /^#[0-9a-fA-F]{6}$/.test(form.secondary_color) ? form.secondary_color : DEFAULT_BRANDING.secondary_color;
  const previewAccent = /^#[0-9a-fA-F]{6}$/.test(form.accent_color) ? form.accent_color : DEFAULT_BRANDING.accent_color;

  const renderImageField = (kind, label, help) => {
    const field = `${kind}_url`;
    return (
      <div className="space-y-2" key={kind}>
        <Label className="text-zinc-300">{label}</Label>
        <div className="flex items-center gap-3">
          <div className="h-14 w-20 rounded border border-zinc-700 bg-zinc-900 flex items-center justify-center overflow-hidden">
            {form[field] ? <img src={form[field]} alt={label} className="max-h-full max-w-full object-contain" /> : <ImageIcon className="h-5 w-5 text-zinc-600" />}
          </div>
          <input ref={fileRefs[kind]} type="file" accept="image/*,.ico" className="hidden" onChange={(e) => handleUpload(kind, e.target.files?.[0])} data-testid={`branding-${kind}-file`} />
          <Button type="button" variant="outline" size="sm" onClick={() => fileRefs[kind].current?.click()} disabled={uploading === kind} className="border-zinc-700 text-zinc-300" data-testid={`branding-${kind}-upload-btn`}>
            {uploading === kind ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
          </Button>
          {form[field] && (
            <Button type="button" variant="ghost" size="sm" onClick={() => resetField(field)} className="text-zinc-400" data-testid={`branding-${kind}-reset`}>
              <RotateCcw className="h-4 w-4" /> Default
            </Button>
          )}
        </div>
        <Input value={form[field] || ''} onChange={(e) => set(field, e.target.value)} placeholder="https://... (or upload)" className="bg-zinc-900 border-zinc-700 text-white text-xs" data-testid={`branding-${kind}-url`} />
        {help && <p className="text-xs text-zinc-500">{help}</p>}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="branding-settings">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Palette className="h-5 w-5 text-navigate-gold" /> Branding
          </h3>
          <p className="text-sm text-zinc-400">
            Applied to every page, the login screen, admin panel, emails and certificates. Leave a field empty to use the default.
            {updatedAt && <span className="block text-xs text-zinc-500 mt-1">Last saved {new Date(updatedAt).toLocaleString()}</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setForm(saved)} disabled={!changed.length || saving} className="border-zinc-700 text-zinc-300" data-testid="branding-discard-btn">
            Discard
          </Button>
          <Button onClick={handleSave} disabled={!changed.length || saving} className="bg-navigate-gold text-black hover:bg-navigate-gold-light" data-testid="branding-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save{changed.length ? ` (${changed.length})` : ''}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          {/* Identity */}
          <section className="p-5 rounded-xl border border-zinc-800 bg-zinc-900/40 space-y-4">
            <h4 className="text-sm font-semibold text-white flex items-center gap-2"><Type className="h-4 w-4 text-navigate-gold" /> Site identity & content</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="site_name" className="text-zinc-300">Site name</Label>
                <Input id="site_name" value={form.site_name || ''} onChange={(e) => set('site_name', e.target.value)} placeholder="Cerebro Core" className="bg-zinc-900 border-zinc-700 text-white" data-testid="branding-site-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="header_tagline" className="text-zinc-300">Header tagline</Label>
                <Input id="header_tagline" value={form.header_tagline || ''} onChange={(e) => set('header_tagline', e.target.value)} placeholder="A Workforce Capability Platform" className="bg-zinc-900 border-zinc-700 text-white" data-testid="branding-tagline" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="footer_text" className="text-zinc-300">Footer text</Label>
                <textarea id="footer_text" value={form.footer_text || ''} onChange={(e) => set('footer_text', e.target.value)} rows={2} placeholder="Shown in the site footer and at the bottom of emails" className="w-full rounded-md bg-zinc-900 border border-zinc-700 text-white text-sm p-2" data-testid="branding-footer-text" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="support_email" className="text-zinc-300">Support email</Label>
                <Input id="support_email" value={form.support_email || ''} onChange={(e) => set('support_email', e.target.value)} placeholder="support@example.com" className="bg-zinc-900 border-zinc-700 text-white" data-testid="branding-support-email" />
              </div>
            </div>
          </section>

          {/* Images */}
          <section className="p-5 rounded-xl border border-zinc-800 bg-zinc-900/40 grid grid-cols-1 md:grid-cols-3 gap-5">
            {renderImageField('logo', 'Logo', 'Header, footer, login, admin panel, emails, certificates')}
            {renderImageField('favicon', 'Favicon', 'Browser tab icon (PNG/ICO, 32-64px)')}
            {renderImageField('login_background', 'Login background', 'Right-hand image on the login page')}
          </section>

          {/* Colours */}
          <section className="p-5 rounded-xl border border-zinc-800 bg-zinc-900/40 space-y-4">
            <h4 className="text-sm font-semibold text-white flex items-center gap-2"><Palette className="h-4 w-4 text-navigate-gold" /> Colours</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <ColorField id="branding-primary" label="Primary" value={form.primary_color || ''} onChange={(v) => set('primary_color', v)} help="Buttons, links, highlights" />
              <ColorField id="branding-secondary" label="Secondary" value={form.secondary_color || ''} onChange={(v) => set('secondary_color', v)} help="Page background & panels (keep dark for readability)" />
              <ColorField id="branding-accent" label="Accent" value={form.accent_color || ''} onChange={(v) => set('accent_color', v)} help="Secondary highlights & progress" />
            </div>
            <div className="flex flex-wrap gap-2">
              {COLOR_PRESETS.map((c) => (
                <button key={c} type="button" onClick={() => set('primary_color', c)} title={`Primary ${c}`} className="h-7 w-7 rounded-full border-2 border-zinc-700 hover:scale-110 transition-transform" style={{ backgroundColor: c }} data-testid={`branding-preset-${c.slice(1)}`} />
              ))}
            </div>
          </section>

          {/* Typography & buttons */}
          <section className="p-5 rounded-xl border border-zinc-800 bg-zinc-900/40 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Heading font</Label>
              <select value={form.heading_font || ''} onChange={(e) => set('heading_font', e.target.value)} className="w-full h-10 rounded-md bg-zinc-900 border border-zinc-700 text-white px-2 text-sm" data-testid="branding-heading-font">
                <option value="">Default (Outfit)</option>
                {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Body font</Label>
              <select value={form.body_font || ''} onChange={(e) => set('body_font', e.target.value)} className="w-full h-10 rounded-md bg-zinc-900 border border-zinc-700 text-white px-2 text-sm" data-testid="branding-body-font">
                <option value="">Default (Inter)</option>
                {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-1"><Square className="h-3.5 w-3.5" /> Button style</Label>
              <div className="flex gap-2 flex-wrap">
                {BUTTON_STYLES.map((b) => (
                  <button key={b.id} type="button" onClick={() => set('button_style', b.id)}
                    className={`flex-1 h-10 text-xs border ${form.button_style === b.id ? 'border-navigate-gold text-navigate-gold bg-navigate-gold/10' : 'border-zinc-700 text-zinc-400'}`}
                    style={{ borderRadius: b.radius }} data-testid={`branding-button-${b.id}`}>
                    {b.label}
                  </button>
                ))}
              </div>
            </div>
          </section>
        </div>

        {/* Live preview */}
        <aside className="p-5 rounded-xl border border-zinc-800 h-fit sticky top-4" style={{ backgroundColor: previewSecondary }} data-testid="branding-preview">
          <p className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Preview</p>
          <div className="flex items-center gap-3 mb-4">
            {form.logo_url ? <img src={form.logo_url} alt="logo" className="h-10 w-auto" /> : <img src={DEFAULT_BRANDING.logo_url} alt="logo" className="h-10 w-auto" />}
            <div>
              <div className="font-bold text-lg" style={{ color: previewPrimary, fontFamily: `'${form.heading_font || 'Outfit'}'` }}>{form.site_name || DEFAULT_BRANDING.site_name}</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">{form.header_tagline}</div>
            </div>
          </div>
          <h5 className="text-white font-semibold mb-1" style={{ fontFamily: `'${form.heading_font || 'Outfit'}'` }}>Course title</h5>
          <p className="text-sm text-zinc-400 mb-4" style={{ fontFamily: `'${form.body_font || 'Inter'}'` }}>Body text uses the selected body font.</p>
          <div className="h-2 rounded-full bg-zinc-800 mb-4 overflow-hidden"><div className="h-full w-2/3" style={{ backgroundColor: previewAccent }} /></div>
          <div className="flex gap-2">
            <span className="px-4 py-2 text-sm font-medium text-black" style={{ backgroundColor: previewPrimary, borderRadius: radius }}>Primary button</span>
            <span className="px-4 py-2 text-sm border" style={{ borderColor: previewPrimary, color: previewPrimary, borderRadius: radius }}>Outline</span>
          </div>
          {form.footer_text && <p className="text-[11px] text-zinc-500 mt-4 border-t border-zinc-800 pt-3">{form.footer_text}</p>}
        </aside>
      </div>
    </div>
  );
}
